import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChipsGridRowComponent, ConfirmDialogComponent, MatSelectSearchComponent, TaggingOptions, UiKitModule } from '@serviceplan/ui-kit';
import {
  ActionsModel,
  AspectRationFormatTree,
  AssetTagsService,
  ClientCoreModule,
  ClientEnvironment,
  CommonUtilityLib,
  CoreAssetsTagsService,
  ENVIRONMENT,
  FileObject,
  FormArrayMapping,
  FormControlModel,
  LufthansaGroupID,
  PROMPTS,
  StepperGroupModel,
  StepperModel,
  TagsDataConfig,
  addTagToArray,
  anyAsset,
  minDateValidator,
} from '@thehubz/client/core';
import { AssetDetails, Collection, CompanyBrand, Tags, TagsApiService, TagsWithAssetCount, User } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata, MULTI_VALUE } from '@thehubz/shared/model';
import { BehaviorSubject, Observable, Subject, Subscription, tap } from 'rxjs';

import {
  getBrandMarkeListSEAT,
  getBrandModelListSEAT,
  getDriveListSEAT,
  getProductList,
  getSpecialModelListSEAT,
  getSubCategoryList,
  getTrimListSEAT,
} from '../../customer/customer-metadata-config';
import { AssetCommonService } from '../../services/asset-common.service';
import { AssetRelationsFormControlComponent } from '../asset-detail-view/asset-relations-form-control/asset-relations-form-control.component';
import { AssetStepperDialogFormService } from '../asset-settings-stepper/asset-stepper-dialog-form.service';
import { AssetStepperDialogService } from '../asset-settings-stepper/asset-stepper-dialog.service';
import { AssetCustomControlComponent } from '../assets/asset-custom-control/asset-custom-control.component';
import { CumulatedTagsChipComponent } from '../tags-dialog/cumulated-tags-chip/cumulated-tags-chip.component';
import { TagsChipComponent } from '../tags-dialog/tags-chip/tags-chip.component';

@Component({
  selector: 'thehubz-asset-edit-stepper',
  templateUrl: './asset-edit-stepper.component.html',
  styleUrls: ['./asset-edit-stepper.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatTooltipModule,
    MatPaginatorModule,
    ClientCoreModule,
    UiKitModule,
    TagsChipComponent,
    CumulatedTagsChipComponent,
    AssetCustomControlComponent,
    AssetRelationsFormControlComponent,
  ],
})
export class AssetEditStepperComponent implements OnChanges, OnDestroy {
  @ViewChild(MatSelectSearchComponent) matSelectSearchComp?: MatSelectSearchComponent;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(TagsChipComponent) tagsChipComponent!: TagsChipComponent;
  @ViewChild(ChipsGridRowComponent) tagsChipsGridRow!: ChipsGridRowComponent;
  @ViewChild(AssetRelationsFormControlComponent) assetRelationsFormControl!: AssetRelationsFormControlComponent;

  @Input() cumulatedTags: AnyType;
  @Input() tagsInfo?: AnyType[];
  @Input() relationData?: AnyType;
  @Input() stepperType?: string;
  @Input() domainsMatchingFlag?: boolean;
  @Input() applicationData!: AnyType;
  @Input() existingAssetListFiles!: AnyType;
  @Input() selectedAssetIds!: string[];
  @Input() selectedAssetRelations?: AnyType;

  @Input() stepperModel!: {
    stepperData: StepperModel;
    additionalData?: AnyType[];
    callFrom?: string;
    editable?: boolean;
  };

  disableAddRow: FormArrayMapping = {
    0: true,
  };
  brandListMapping: FormArrayMapping = {};
  currentFormFieldName!: string;
  formArrayControlLength!: number;
  textIsRequired = PROMPTS.TEXT.IS_REQUIRED;
  textAreRequired = PROMPTS.TEXT.ARE_REQUIRED;
  minLengthText = PROMPTS.TEXT.MIN_LENGTH;

  tagPlaceHolderText = $localize`Search tags...`;
  chipInputTooltipText = $localize`Press \'Enter\' or \'Comma\' to add tags`;
  collectionTagPlaceHolderText = $localize`Add tags...`;
  stepperHeader!: string;
  multipleAssetRelationFlag = false;
  disableButtons = true;
  isMatTreeSelected = false;
  fileUploadRequired = false;
  formControlValidState = false;
  invalidFormState = new BehaviorSubject<boolean>(false);
  initialStepper!: StepperModel;
  stepperFormGroups: FormGroup = this._formBuilder.group({});
  assetDetails: AssetDetails[] = [];
  matTreeRawData: AspectRationFormatTree[] = [];
  users: User[] = [];
  assetUrlService: AnyType;
  currentAssetType?: string;
  taggingOptions: TaggingOptions = {
    roles: ['global.library', 'asset.update'],
    callFrom: 'asset-edit-stepper',
  };
  tagToHighlight!: string;
  // set mat tree checkbox to true xon load
  matTreeCheckbox = false;
  readonly currentUserID!: string;
  /** Tags for chip display */
  tags: string[] = [];
  /** Auto complete tag data */
  autoCompTags: Tags[] = [];
  allTagsListOption: Tags[] = [];
  collectionTags: string[] = [];
  // list of user email from input mat-chip
  listUsers: string[] = [];
  /* File uploads and assets vars */
  files: FileObject[] = [];
  progress = 0;
  currentUploadFileName = '';
  collectionsList: Collection[] = [];
  /** Paginator object */
  paginatorObj: Partial<PageEvent> & Pick<PageEvent, 'pageIndex' | 'pageSize'> = {
    pageIndex: 0,
    pageSize: 15,
  };
  tagSub?: Subscription;
  tagData!: TagsDataConfig;

  private isInitialized = false;
  readonly brandingKey!: CustomClients;
  readonly AnyAsset = anyAsset;
  readonly CustomClients = CustomClients;

  @Output() ready = new EventEmitter<boolean>();
  @Output() formSubmit = new EventEmitter<AnyType>();
  @Output() updateTagObject = new EventEmitter<AnyType>();
  @Output() stepChange = new EventEmitter<StepperGroupModel>();
  @Output() taggedUser = new EventEmitter<AnyType>();
  @Output() descriptionText = new EventEmitter<AnyType>();

  /** Mat-paginator properties */
  /** Number of items to display on a page */
  dataSource!: MatTableDataSource<TagsWithAssetCount>;
  /** Connect with the MatTableDataSource */
  tagsObs!: Observable<TagsWithAssetCount[]>;
  /** Store filter search text */
  searchText = '';
  /** Store selected tag list */
  searchTagsList: TagsWithAssetCount[] = [];
  filterObject: AnyType = {
    filters: {},
  };
  formFieldVisibility: Record<string, boolean[]> = {};
  /**
   * Observable to emit when the component is destroyed.
   */
  private _destroyed$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AnyType,
    @Inject(ENVIRONMENT) private env: ClientEnvironment,
    private el: ElementRef,
    private readonly dialog: MatDialog,
    private readonly _formBuilder: FormBuilder,
    private readonly tagsApiService: TagsApiService,
    private readonly commonUtilityLib: CommonUtilityLib,
    private readonly assetTagsService: AssetTagsService,
    private readonly assetCommonService: AssetCommonService,
    private readonly formService: AssetStepperDialogFormService,
    private readonly coreAssetsTagsService: CoreAssetsTagsService,
    private readonly dialogRef: MatDialogRef<AssetEditStepperComponent>,
    private readonly assetStepperDialogService: AssetStepperDialogService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
    this.currentUserID = assetCommonService.currentUser.userData._id;
  }

  private init(): void {
    this.currentAssetType = this.existingAssetListFiles[0] && this.existingAssetListFiles[0].assetType;
    this.initialStepper = JSON.parse(JSON.stringify(this.stepperModel.stepperData));
    this.assetUrlService = this.dialogRef.componentInstance.assetUrlService;
    const stepperInfo = Object.assign({}, ...this.initialStepper.stepper.filter((step: StepperGroupModel) => step.selected));
    this.stepperHeader = !stepperInfo.hideHeader ? stepperInfo.stepperName : '';
    if (this.existingAssetListFiles.length > 1 && this.stepperType === 'relations') {
      this.multipleAssetRelationFlag = true;
    }
    this.assetStepperDialogService.resetAllSteps(this.initialStepper, 0);
    this.initStepperFormGroups();
    this.isInitialized = true;

    if ([CustomClients.LufthansaMediacenter, CustomClients.LufthansaMedialounge].includes(this.brandingKey)) {
      this.chipInputTooltipText = '';
    }
    this.tagData = {
      filters: this.filterObject.filters,
      selectedTags: this.tagsInfo,
      callFrom: 'child',
    };

    if (this.stepperFormGroups.controls[AssetMetadata.General.TAGS]) {
      this.tagSub = this.assetTagsService
        .fetchTagsForAutoComp(this.stepperFormGroups.controls[AssetMetadata.General.TAGS].get(AssetMetadata.General.TAGS))
        ?.pipe(
          tap((data) => {
            this.autoCompTags = data;
            this.tagToHighlight = this.assetTagsService.accessTagName();
          }),
        )
        .subscribe();
    }
    if (this.stepperFormGroups.controls[AssetMetadata.General.TAGS] && this.brandingKey === CustomClients.VKB) {
      this.tagsApiService.tagsControllerFindAll().subscribe((data) => {
        if (data) this.allTagsListOption = data;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes[AssetMetadata.Others.STEPPER_MODEL] && changes[AssetMetadata.Others.STEPPER_MODEL].currentValue && !this.isInitialized) {
      this.init();
    }
  }

  ngOnDestroy(): void {
    if (this.tagSub) this.tagSub.unsubscribe();

    this._destroyed$.next();
    this._destroyed$.complete();
  }

  initStepperFormGroups() {
    this.stepperFormGroups = new FormGroup({});

    if (this.initialStepper) {
      const stepperFormGroups = this.formService.createStepperFormGroups(this.initialStepper['stepper'], this.formFieldVisibility);
      this.stepperFormGroups = stepperFormGroups.stepperFormGroups;
      this.tags = stepperFormGroups.tagsArray;
    }
    setTimeout(() => {
      this.patchFormData();
    }, 100);
  }

  patchFormData() {
    if (this.initialStepper['stepper'] && this.initialStepper['stepper'].length > 0) {
      this.initialStepper['stepper'].forEach((stepper: StepperGroupModel) => {
        const formGroupName = stepper.stepperGroupName;
        const formGroupField = this.stepperFormGroups.controls[formGroupName] as UntypedFormGroup;
        if (stepper?.formFields && stepper.formFields?.length > 0) {
          stepper?.formFields.forEach((formField: FormControlModel) => {
            const formFieldName = formField.formFieldName;
            const formFieldControl = formGroupField.controls[formFieldName] as UntypedFormControl;
            if (this.applicationData && this.applicationData[formFieldName]) {
              if (formField.fieldType == 'dropdown') {
                formFieldControl.patchValue(this.applicationData[formFieldName]);
                if (this.applicationData[formFieldName]._id !== MULTI_VALUE) {
                  const isMultiValue = formField.options?.some((option) => option._id === MULTI_VALUE || option.id === MULTI_VALUE);
                  if (isMultiValue) this.removeMultipleValueOption(formField);
                }
                if (formFieldName === 'agency' || formFieldName === 'client' || formFieldName === 'brand') {
                  if (this.applicationData[formFieldName]._id) {
                    const fieldId = this.applicationData[formFieldName]._id;
                    let idx: AnyType = -1;
                    idx = formField.options?.findIndex((option) => option._id === fieldId);
                    if (idx != -1 && formField && formField.options) {
                      const selectedOption = formField.options[idx];
                      const eventValue = { value: selectedOption };
                      this.onValueChange(eventValue, formFieldName, formField, stepper);
                    }
                  }
                }
              } else if (formField.fieldType == 'multiSelectChip') {
                let fieldDataValue: AnyType[] = [];
                const applicationDataValue = this.applicationData[formFieldName];
                if (applicationDataValue && applicationDataValue.length > 0) {
                  applicationDataValue.forEach((value: AnyType) => {
                    if (!value._id && !value.id) {
                      const tempValue: AnyType[] = formField.options
                        ? formField.options?.filter((option) => value === option._id || value === option.id || value === option)
                        : [];
                      fieldDataValue = [...fieldDataValue, ...tempValue];
                    }
                  });
                }
                formFieldControl.patchValue(fieldDataValue);
              } else if (formField.fieldType == 'formArray') {
                if (formFieldName === 'domains') {
                  const formArrayFieldControl = formGroupField.controls[formFieldName] as UntypedFormArray;
                  this.applicationData[formFieldName].forEach((domain: AnyType, index: number) => {
                    if (formArrayFieldControl.controls[index]) {
                      this.updateFormArrayControlsData(formArrayFieldControl, index, domain, formField, stepper);
                    } else {
                      this.addRow(index, formGroupName, formFieldName, formField.formFieldGroup);
                      if (formArrayFieldControl.controls[index]) {
                        this.updateFormArrayControlsData(formArrayFieldControl, index, domain, formField, stepper);
                      }
                    }
                  });
                }
              } else {
                if (formFieldName == 'name') {
                  if (this.existingAssetListFiles.length === 1) {
                    formField.disable = false;
                    formFieldControl.enable();
                  }
                  formFieldControl.patchValue(this.applicationData[formFieldName]);
                } else if (formFieldName == AssetMetadata.General.TAGS) {
                  formFieldControl.patchValue(this.applicationData[formFieldName]);
                  this.tags = this.applicationData[formFieldName];
                  this.assetTagsService.updateTagsArray(this.tags);
                } else {
                  if (formFieldName === 'plannedDeletion' && this.applicationData['plannedDeletion']) {
                    formField.disable = false;
                    formFieldControl.enable();
                  }
                  formFieldControl.patchValue(this.applicationData[formFieldName]);
                }
              }
            } else {
              if (formField.fieldType == 'checkbox') {
                if (formFieldName == 'plannedDeletionToggle') {
                  if (this.applicationData['plannedDeletion']) {
                    formFieldControl.patchValue(true);
                  } else {
                    formField.visible = false;
                  }
                } else if (formFieldName == 'noExpiry') {
                  let selectedFlag = true;
                  if (this.applicationData['expiryDate']) {
                    selectedFlag = false;
                  }
                  this.checkboxChanged({ checked: selectedFlag }, formFieldName, stepper['stepperGroupName']);
                }
              } else {
                if (formFieldName === 'plannedDeletion') {
                  formField.visible = false;
                }
                if (
                  this.applicationData &&
                  this.applicationData['customFields'] &&
                  (this.applicationData['customFields'][formFieldName] ||
                    (this.applicationData['customFields'][formGroupName] && this.applicationData['customFields'][formGroupName][formFieldName]))
                ) {
                  const currentFieldValue =
                    this.applicationData['customFields'][formFieldName] || this.applicationData['customFields'][formGroupName][formFieldName];
                  if (formField.fieldType == 'multiSelectChip') {
                    let fieldDataValue: AnyType[] = [];
                    const groupListDataValue: AnyType[] = [];
                    const applicationDataValue = currentFieldValue;
                    if (applicationDataValue && applicationDataValue.length > 0) {
                      applicationDataValue.forEach((value: AnyType) => {
                        if (!value._id && !value.id) {
                          const tempValue: AnyType[] = formField.options
                            ? formField.options?.filter((option) => {
                                if (option.lists && option.lists.length > 0) {
                                  option.lists.forEach((listOption: AnyType) => {
                                    if (value === listOption._id) {
                                      const listId = groupListDataValue?.findIndex((option) => option._id === listOption._id);
                                      if (listId === -1) {
                                        groupListDataValue.push(listOption);
                                      }
                                    }
                                  });
                                  return false;
                                } else {
                                  return value === option._id || value === option.id || value === option;
                                }
                              })
                            : [];
                          fieldDataValue = [...fieldDataValue, ...tempValue];
                        }
                      });
                    }
                    if (formField.groupListView) {
                      formFieldControl.patchValue(groupListDataValue);
                    } else {
                      formFieldControl.patchValue(fieldDataValue);
                    }
                  } else if (formField.fieldType === 'formArray') {
                    const formArrayFieldControl = formGroupField.controls[formFieldName] as UntypedFormArray;
                    currentFieldValue.forEach((data: AnyType, index: number) => {
                      if (formArrayFieldControl.controls[index]) {
                        this.updateFormArrayControlsDetailsForMakeos(formArrayFieldControl, index, data, formField);
                      } else {
                        this.addRow(index, formGroupName, formFieldName, formField.formFieldGroup);
                        if (formArrayFieldControl.controls[index]) {
                          this.updateFormArrayControlsDetailsForMakeos(formArrayFieldControl, index, data, formField);
                        }
                      }
                    });
                  } else {
                    formFieldControl.patchValue(currentFieldValue);
                  }
                }
                if (formField.fieldType == 'dropdown') {
                  if (
                    this.applicationData &&
                    this.applicationData['customFields'] &&
                    (this.applicationData['customFields'][formFieldName] ||
                      (this.applicationData['customFields'][formGroupName] && this.applicationData['customFields'][formGroupName][formFieldName]))
                  ) {
                    const currentFieldValue =
                      this.applicationData['customFields'][formFieldName] || this.applicationData['customFields'][formGroupName][formFieldName];
                    if (currentFieldValue !== MULTI_VALUE) {
                      this.removeMultipleValueOption(formField);
                    }
                  }
                  if (
                    formFieldName == 'product' ||
                    formFieldName == 'subcategory' ||
                    formFieldName == 'channel' ||
                    formFieldName == 'lufthansaContent' ||
                    formFieldName == 'lufthansaProduct'
                  ) {
                    // Below is hard coded check for Lufthansa Group Client, in order to show taxonomy fields
                    if (
                      this.brandingKey === CustomClients.Standard &&
                      (formFieldName == 'lufthansaContent' || formFieldName == 'lufthansaProduct') &&
                      this.applicationData['client'] &&
                      this.applicationData['client']._id !== '67d80341f77a6157b1847e60'
                    ) {
                      formFieldControl.patchValue('');
                      this.applicationData['customFields'][formFieldName] = '';
                    }
                    if (this.applicationData && this.applicationData['customFields'] && this.applicationData['customFields'][formFieldName]) {
                      const fieldId = this.applicationData['customFields'][formFieldName];
                      let idx: AnyType = -1;
                      idx = formField.options?.findIndex((option) => option._id === fieldId);
                      if (idx != -1 && formField && formField.options) {
                        const selectedOption = formField.options[idx];
                        const eventValue = { value: selectedOption };
                        this.onValueChange(eventValue, formFieldName, formField, stepper);
                      }
                    }
                  }
                }
              }
            }
          });
        }
      });
    }
  }

  updateFormArrayControlsDetailsForMakeos(formArrayFieldControl: UntypedFormArray, index: number, data: AnyType, formField: FormControlModel) {
    const dataFieldControls = formArrayFieldControl.controls[index] as UntypedFormGroup;
    if (dataFieldControls) {
      for (const formKey in dataFieldControls.controls) {
        if (data[formKey]) {
          const fieldId = data[formKey];
          let idx: AnyType = -1;
          const currentField = formField?.formFieldGroup?.find((field: AnyType) => {
            return field.formFieldName === formKey;
          });
          idx = currentField?.options?.findIndex((option) => option._id === fieldId);
          if (idx != -1 && formField && currentField) {
            dataFieldControls.controls[formKey].patchValue(data[formKey]);
          }
        }
      }
    }
  }

  updateFormArrayControlsData(formArrayFieldControl: UntypedFormArray, index: number, domain: AnyType, formField: FormControlModel, formGroupField: AnyType) {
    const domainFieldControls = formArrayFieldControl.controls[index] as UntypedFormGroup;
    if (domainFieldControls) {
      for (const formKey in domainFieldControls.controls) {
        if (
          formKey === 'agency' ||
          formKey === 'client' ||
          formKey === 'brand' ||
          formKey === 'product' ||
          formKey === 'category' ||
          formKey === 'brandMarke' ||
          formKey === 'brandModel' ||
          formKey === 'subcategory'
        ) {
          if (domain[formKey]) {
            const fieldId = domain[formKey];
            let idx: AnyType = -1;
            const currentField = formField?.formFieldGroup?.find((field: AnyType) => {
              return field.formFieldName === formKey;
            });
            idx = currentField?.options?.findIndex((option) => option._id === fieldId);
            if (idx != -1 && formField && currentField && currentField.options) {
              domainFieldControls.controls[formKey].patchValue(domain[formKey]);
              if (!this.domainsMatchingFlag) {
                const idx = currentField.options.findIndex((options: AnyType) => options._id == MULTI_VALUE);
                if (idx == -1 && this.existingAssetListFiles.length > 1) {
                  currentField.options.push({ _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE });
                }
                domainFieldControls.controls[formKey].patchValue({ _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE });
                domainFieldControls.controls[formKey].disable();
              }
              const selectedOption = currentField.options[idx];
              const eventValue = { value: selectedOption };
              this.formArrayValueChange(eventValue, formKey, formField, index, formGroupField);
            }
          }
        }
      }
    }
  }

  removeMultipleValueOption(formField: FormControlModel) {
    if (formField.options && formField.options?.length > 0) {
      const filterOptions = formField.options.filter((option) => option._id !== MULTI_VALUE && option.id !== MULTI_VALUE);
      formField.options = filterOptions;
    }
  }

  /**
   * This function is used for formArray. Mainly for the milestone. It will return the form control based on field name
   * @param fieldArrayName
   * @param formGroupName
   * @returns
   */
  getControls(fieldArrayName: AnyType, formGroupName: AnyType) {
    if (this.stepperFormGroups.controls[formGroupName]) {
      const formGroup = this.stepperFormGroups.controls[formGroupName] as UntypedFormGroup;
      const formArray = formGroup.controls[fieldArrayName] as UntypedFormArray;
      this.formArrayControlLength = formArray.controls.length;
      return formArray.controls;
    }

    return [];
  }

  /**
   * Add controls to form array
   * @param formFieldName
   * @param stepperGroupName
   * @param formFieldGroup
   */
  addRow(i: number, stepperGroupName: string, formFieldName: string, formFieldGroup?: FormControlModel[]) {
    const currentFormArray = this.stepperFormGroups.controls[stepperGroupName].get(formFieldName) as UntypedFormArray;
    currentFormArray.push(this.formService.buildFormGroup(formFieldGroup));
    this.disableAddRow[i + 1] = true;
    formFieldGroup?.forEach((fieldGrp) => {
      this.formFieldVisibility[fieldGrp.formFieldName].splice(i + 1, 0, fieldGrp.visible);
      if (fieldGrp?.nestedOptions && fieldGrp?.nestedOptions[i + 1]) {
        delete fieldGrp.nestedOptions[i + 1];
      }
    });
  }

  getIsLastIndex(index: number, stepperGroupName: string, formFieldName: string): boolean {
    const currentFormArray = this.stepperFormGroups.controls[stepperGroupName].get(formFieldName) as UntypedFormArray;
    return index === currentFormArray?.value?.length - 1;
  }

  /**
   * Remove controls from form array
   * @param i
   * @param formFieldName
   * @param stepperGroupName
   */
  removeRow(i: number, stepperGroupName: string, formFieldName: string, formFieldGroup?: FormControlModel[]) {
    const currentFormArray = this.stepperFormGroups.controls[stepperGroupName].get(formFieldName) as UntypedFormArray;
    delete this.disableAddRow[i];
    if (currentFormArray.length > 1) {
      currentFormArray.removeAt(i);
      // Reset visibility of fields in next row after deleting
      currentFormArray.controls.forEach((formGroup) => {
        const group = formGroup as FormGroup;
        // Loop through each control within the form group
        Object.keys(group.controls).forEach((controlName) => {
          const control = formGroup.get(controlName);
          control?.markAsDirty();
          control?.markAsTouched();
        });
      });
      if (this.brandingKey === CustomClients.Vorwerk) {
        delete this.brandListMapping[i];
        let counter = 0;
        for (const key in this.brandListMapping) {
          if (+key > i) {
            this.brandListMapping[counter] = this.brandListMapping[key];
          }
          counter++;
        }
      }
      formFieldGroup?.forEach((fieldGrp: AnyType) => {
        this.formFieldVisibility[fieldGrp.formFieldName].splice(i, 1);
        if (fieldGrp?.nestedOptions && fieldGrp?.nestedOptions[i]) {
          delete fieldGrp.nestedOptions[i];
          let counter1 = 0;
          for (const key1 in fieldGrp.nestedOptions) {
            if (+key1 > i) {
              fieldGrp.nestedOptions[counter1] = fieldGrp.nestedOptions[key1];
            }
            counter1++;
          }
        }
      });
    } else {
      this.matSelectSearchComp?.clearLabel();
    }
  }

  formArrayValueChange(event: AnyType, formFieldName: string, formField: FormControlModel, rowIndex: number, formGroupField?: StepperGroupModel) {
    this.currentFormFieldName = formFieldName;
    if (this.brandingKey === CustomClients.Vorwerk) {
      switch (formFieldName) {
        case 'agency': {
          const client: Pick<CompanyBrand, '_id' | 'name'>[] = event.value.clients;
          const clientFormField = formField.formFieldGroup?.find((field) => field.formFieldName === 'client');
          if (clientFormField) this.brandListMapping[rowIndex] = client;
          break;
        }
        case 'client': {
          const brand: Pick<CompanyBrand, '_id' | 'name'>[] = event.value.brands;
          const brandFormField = formField.formFieldGroup?.find((field) => field.formFieldName === 'brand');
          if (brandFormField) this.brandListMapping[rowIndex] = brand;
          break;
        }
      }
    }
    setTimeout(() => {
      this.changeDisableAddRowButton(rowIndex, 'domains', formGroupField?.stepperGroupName);
    }, 0);
    this.onValueChange(event, formFieldName, formField, formGroupField, rowIndex);
  }

  checkFromArrayValue(i: number, stepperGroupName: string, formArrayFieldName: string, formFieldName: string) {
    const currentFormArray = this.stepperFormGroups.controls[stepperGroupName].get(formArrayFieldName) as UntypedFormArray;
    if (currentFormArray.at(i).errors?.['duplicate'] && currentFormArray.at(i).get(formFieldName)?.value) return true;
    else return false;
  }

  checkFromArrayRequiredValue(i: number, stepperGroupName: string, formArrayFieldName: string, formFieldName: string) {
    const currentFormArray = this.stepperFormGroups.controls[stepperGroupName].get(formArrayFieldName) as UntypedFormArray;
    if (currentFormArray.at(i).get(formFieldName)?.errors?.['required']) return true;
    else return false;
  }

  changeDisableAddRowButton(rowIndex: number, formFieldName?: string, stepperGroupName?: string) {
    const stepperFormGroupsControls = this.stepperFormGroups.controls[stepperGroupName || ''].value;
    let isEmpty = false;
    if (stepperFormGroupsControls[formFieldName || '']) {
      isEmpty = Object.values(stepperFormGroupsControls[formFieldName || ''][rowIndex]).every((x) => x === null || x === '' || x === undefined || x === false);
    }
    this.disableAddRow[rowIndex] = isEmpty;
  }

  onValueChange(event: AnyType, formFieldName: string, formField: FormControlModel, formGroupField?: AnyType, rowIndex?: number) {
    const filterName = event.value?.name;
    switch (formFieldName) {
      case 'agency': {
        this.filterObject.filters = {
          ...this.filterObject.filters,
          ['agency.name']: filterName,
        };
        this.onAgencySelected(event, formField, formGroupField, rowIndex);
        break;
      }
      case 'client': {
        this.filterObject.filters = {
          ...this.filterObject.filters,
          'client.name': filterName,
        };
        this.onClientSelected(event, formField, formGroupField, rowIndex);
        break;
      }
      case 'brand': {
        this.filterObject.filters = {
          ...this.filterObject.filters,
          'brand.name': filterName,
        };
        this.onBrandSelected(event, formField, formGroupField, rowIndex);
        break;
      }
      case 'brandMarke': {
        this.onbrandMarkeSelected(event, formField, formGroupField, rowIndex);
        break;
      }
      case 'lufthansaContent':
        this.onLufthansaContentSelected(event);
        break;
      case 'brandModel': {
        this.onBrandModelSelected(formGroupField);
        break;
      }
      case 'category': {
        this.onCategorySelected(event, formField, formGroupField, rowIndex);
        break;
      }
      case 'product': {
        this.onProductSelected(event, formField, formGroupField, rowIndex);
        break;
      }
      case 'channel': {
        this.onChannelSelected(event, formField, formGroupField);
        break;
      }
    }
  }

  onAgencySelected(event: AnyType, formField: FormControlModel, formGroup?: AnyType, rowIndex?: number): void {
    const clients = event.value.clients;
    let clientField: AnyType;
    let brandField: AnyType;
    if (formField.fieldType == 'formArray') {
      clientField = formField?.formFieldGroup?.find((field: AnyType) => field.formFieldName === 'client');
      brandField = formField?.formFieldGroup?.find((field: AnyType) => field.formFieldName === 'brand');
    } else {
      clientField = formGroup?.formFields?.find((field: AnyType) => field.formFieldName === 'client');
      brandField = formGroup?.formFields?.find((field: AnyType) => field.formFieldName === 'brand');
    }
    if (clientField) {
      clientField.value = '';
      this.setFormFieldValue(formGroup.stepperGroupName, formField.formFieldName, rowIndex, 'client');
      const idx = clients.findIndex((options: AnyType) => options._id == MULTI_VALUE);
      if (idx == -1 && this.existingAssetListFiles.length > 1 && rowIndex === undefined) {
        clients.push({ _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE, brands: [] });
      }
      clientField.options = clients;
      if (clientField.nestedOptions && rowIndex != undefined) clientField.nestedOptions[rowIndex] = clients;
    }
    if (brandField && !event.source?.singleValue) {
      brandField.value = '';
      brandField.options = [];
      if (brandField.nestedOptions && rowIndex != undefined) brandField.nestedOptions[rowIndex] = [];
    }
    this.taggingOptions.agency = event.value._id;
  }

  onClientSelected(event: AnyType, formField?: FormControlModel, formGroup?: AnyType, rowIndex?: number): void {
    const brands = event.value.brands;
    let brandField: AnyType;
    if (formField?.fieldType == 'formArray') {
      brandField = formField?.formFieldGroup?.find((field: AnyType) => field.formFieldName === 'brand');
    } else {
      brandField = formGroup?.formFields?.find((field: AnyType) => field.formFieldName === 'brand');
    }
    if (brandField) {
      brandField.value = '';
      const idx = brands.findIndex((options: AnyType) => options._id == MULTI_VALUE);
      if (idx == -1 && this.existingAssetListFiles.length > 1 && rowIndex === undefined) {
        brands.push({ _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE });
      }
      brandField.options = brands;
      if (brandField.nestedOptions && rowIndex != undefined) brandField.nestedOptions[rowIndex] = brands;
    }
    if (this.brandingKey === CustomClients.Vorwerk && rowIndex != undefined && formField) {
      const formGroupField = this.stepperFormGroups.controls[formGroup.stepperGroupName] as UntypedFormGroup;
      const formArrayFieldControl = formGroupField.controls[formField.formFieldName] as UntypedFormArray;
      const brandFieldControl = formArrayFieldControl.controls[rowIndex].get('brand');
      if (brandFieldControl && !brandFieldControl.value) {
        this.formFieldVisibility['category'][rowIndex] = false;
        this.formFieldVisibility['product'][rowIndex] = false;
        this.formFieldVisibility['subcategory'][rowIndex] = false;
      }
    }
    const tagsFormGroup: AnyType = this.initialStepper.stepper.filter((stepper: AnyType) => stepper.stepperGroupName === AssetMetadata.General.TAGS)[0];
    if (tagsFormGroup) {
      const contentFieldIndex = tagsFormGroup.formFields.findIndex((formField: AnyType) => formField.formFieldName === 'lufthansaContent') || -1;
      const productFieldIndex = tagsFormGroup.formFields.findIndex((formField: AnyType) => formField.formFieldName === 'lufthansaProduct') || -1;
      const formGroupField = this.stepperFormGroups.controls[AssetMetadata.General.TAGS] as UntypedFormGroup;
      if (contentFieldIndex > -1 && productFieldIndex > -1) {
        // Below is hard coded check for Lufthansa Group Client, in order to show taxonomy fields
        if (event.value._id === LufthansaGroupID) {
          formGroupField.get('lufthansaContent')?.addValidators(Validators.required);
          formGroupField.get('lufthansaContent')?.updateValueAndValidity();
          tagsFormGroup.formFields[contentFieldIndex].visible = true;
        } else {
          tagsFormGroup.formFields[contentFieldIndex].visible = false;
          tagsFormGroup.formFields[productFieldIndex].visible = false;
          const formGroupField = this.stepperFormGroups.controls[AssetMetadata.General.TAGS] as UntypedFormGroup;
          formGroupField.get('lufthansaContent')?.removeValidators(Validators.required);
          formGroupField.get('lufthansaContent')?.setValue('');
          formGroupField.get('lufthansaContent')?.markAsDirty();
          formGroupField.get('lufthansaContent')?.markAsTouched();
          formGroupField.get('lufthansaContent')?.updateValueAndValidity();
          formGroupField.get('lufthansaProduct')?.removeValidators(Validators.required);
          formGroupField.get('lufthansaProduct')?.setValue('');
          formGroupField.get('lufthansaProduct')?.markAsDirty();
          formGroupField.get('lufthansaProduct')?.markAsTouched();
          formGroupField.get('lufthansaProduct')?.updateValueAndValidity();
        }
      }
    }
    this.taggingOptions.client = event.value._id;
  }

  setFormFieldValue(formGroupName: string, formFieldName: string, rowIndex?: number, currentFieldName?: string) {
    const formGroupField = this.stepperFormGroups.controls[formGroupName] as UntypedFormGroup;
    const formArrayFieldControl = formGroupField.controls[formFieldName] as UntypedFormArray;
    if (rowIndex != undefined && currentFieldName) {
      const formFieldControl = formArrayFieldControl.controls[rowIndex].get(currentFieldName);
      if (formFieldControl) formFieldControl.patchValue('');
    }
  }

  onBrandSelected(event: AnyType, formField?: AnyType, formGroup?: AnyType, rowIndex?: number) {
    this.taggingOptions.brand = event.value && event.value._id;
    if (this.brandingKey === CustomClients.SEAT) {
      const brandMarkeField = formField?.formFieldGroup?.find((field: AnyType) => field.formFieldName === 'brandMarke');
      const brandMarkeList = getBrandMarkeListSEAT();
      brandMarkeField.options = brandMarkeList;
      if (brandMarkeField.nestedOptions && rowIndex != undefined) brandMarkeField.nestedOptions[rowIndex] = brandMarkeList;
    }
    if (this.brandingKey === CustomClients.Vorwerk) {
      const categoryField = formField?.formFieldGroup?.find((field: AnyType) => field.formFieldName === 'category');
      const productField = formField?.formFieldGroup?.find((field: AnyType) => field.formFieldName === 'product');
      const subCategoryField = formField?.formFieldGroup?.find((field: AnyType) => field.formFieldName === 'subcategory');
      subCategoryField.visible = false;
      const categoryList = categoryField.options;
      const productList = getProductList(event.value.name.toLowerCase());
      const idx = categoryList.findIndex((options: AnyType) => options._id == MULTI_VALUE);
      if (idx == -1 && this.existingAssetListFiles.length > 1 && rowIndex === undefined) {
        categoryList.push({ _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE, brands: [] });
      }
      const idx1 = productList.findIndex((options: AnyType) => options._id == MULTI_VALUE);
      if (idx1 == -1 && this.existingAssetListFiles.length > 1 && rowIndex === undefined) {
        productList.push({ _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE, brands: [] });
      }
      categoryField.options = categoryList;
      if (categoryField.nestedOptions && rowIndex != undefined) categoryField.nestedOptions[rowIndex] = categoryList;

      productField.options = productList;
      if (productField.nestedOptions && rowIndex != undefined) productField.nestedOptions[rowIndex] = productList;
      if (rowIndex != undefined) {
        if (event.value) {
          this.formFieldVisibility['category'][rowIndex] = true;
        } else {
          this.formFieldVisibility['product'][rowIndex] = false;
          this.formFieldVisibility['subcategory'][rowIndex] = false;
        }
      }
    }
  }
  onLufthansaContentSelected(event: AnyType) {
    const tagsFormGroup: AnyType = this.initialStepper.stepper.filter((stepper: AnyType) => stepper.stepperGroupName === AssetMetadata.General.TAGS)[0];
    if (tagsFormGroup) {
      const productFieldIndex = tagsFormGroup.formFields.findIndex((formField: AnyType) => formField.formFieldName === 'lufthansaProduct') || -1;
      const formGroupField = this.stepperFormGroups.controls[AssetMetadata.General.TAGS] as UntypedFormGroup;
      if (productFieldIndex > -1) {
        if (event.value._id === 'product') {
          formGroupField.get('lufthansaProduct')?.addValidators(Validators.required);
          formGroupField.get('lufthansaProduct')?.updateValueAndValidity();
          tagsFormGroup.formFields[productFieldIndex].visible = true;
        } else {
          tagsFormGroup.formFields[productFieldIndex].visible = false;
          formGroupField.get('lufthansaProduct')?.removeValidators(Validators.required);
          formGroupField.get('lufthansaProduct')?.setValue('');
          formGroupField.get('lufthansaProduct')?.markAsDirty();
          formGroupField.get('lufthansaProduct')?.markAsTouched();
          formGroupField.get('lufthansaProduct')?.updateValueAndValidity();
        }
      }
    }
  }

  onbrandMarkeSelected(event: AnyType, formField?: AnyType, formGroup?: AnyType, rowIndex?: number) {
    if (this.brandingKey === CustomClients.SEAT) {
      const brandModelField = formField?.formFieldGroup?.find((field: AnyType) => field.formFieldName === 'brandModel');
      const brandModelList = getBrandModelListSEAT(event.value.name.toLowerCase(), true);
      brandModelField.options = brandModelList;
      if (brandModelField.nestedOptions && rowIndex != undefined) brandModelField.nestedOptions[rowIndex] = brandModelList;
    }
  }

  onBrandModelSelected(formGroup: StepperGroupModel) {
    if (this.brandingKey === CustomClients.SEAT) {
      setTimeout(() => {
        const contentDescriptionGroupIndex = this.initialStepper.stepper.findIndex(
          (stepper: StepperGroupModel) => stepper.stepperGroupName === 'contentDescription',
        );
        if (contentDescriptionGroupIndex > -1) {
          const formFields = this.initialStepper.stepper[contentDescriptionGroupIndex].formFields || [];
          const formGroupField = this.stepperFormGroups.controls[formGroup.stepperGroupName] as UntypedFormGroup;
          const brandModelValues: string[] = [];
          const formGroupValue = formGroupField?.getRawValue().domains;
          formGroupValue.forEach((domain: AnyType) => {
            brandModelValues.push(domain.brandModel._id || domain.brandModel);
          });
          const specialModelFormFieldIndex = formFields.findIndex((field: AnyType) => field.formFieldName === 'specialModel');
          const trimFormFieldIndex = formFields.findIndex((field: AnyType) => field.formFieldName === 'trim');
          const driveFormFieldIndex = formFields.findIndex((field: AnyType) => field.formFieldName === 'drive');
          const SpecialModelList = getSpecialModelListSEAT(brandModelValues, true);
          const TrimList = getTrimListSEAT(brandModelValues, true);
          const DriveList = getDriveListSEAT(brandModelValues, true);
          if (specialModelFormFieldIndex !== -1) {
            formFields[specialModelFormFieldIndex].options = SpecialModelList;
            formFields[specialModelFormFieldIndex].disable = !this.domainsMatchingFlag ? true : false;
          }
          if (trimFormFieldIndex !== -1) {
            formFields[trimFormFieldIndex].options = TrimList;
            formFields[trimFormFieldIndex].disable = !this.domainsMatchingFlag ? true : false;
          }
          if (driveFormFieldIndex !== -1) {
            formFields[driveFormFieldIndex].options = DriveList;
            formFields[driveFormFieldIndex].disable = !this.domainsMatchingFlag ? true : false;
          }
        }
      }, 0);
    }
  }

  onCategorySelected(event: AnyType, formField?: AnyType, formGroup?: AnyType, rowIndex?: number) {
    if (this.brandingKey === CustomClients.Vorwerk && rowIndex != undefined) {
      const productField = formField?.formFieldGroup?.find((field: AnyType) => field.formFieldName === 'product');
      if (productField && event.value && event.value._id === 'product') {
        this.formFieldVisibility['product'][rowIndex] = true;
      } else {
        this.formFieldVisibility['product'][rowIndex] = false;
        this.formFieldVisibility['subcategory'][rowIndex] = false;
        this.setFormFieldValue(formGroup.stepperGroupName, formField.formFieldName, rowIndex, 'product');
        this.setFormFieldValue(formGroup.stepperGroupName, formField.formFieldName, rowIndex, 'subcategory');
      }
    }
  }

  onProductSelected(event: AnyType, formField?: AnyType, formGroup?: AnyType, rowIndex?: number) {
    if (this.brandingKey === CustomClients.Vorwerk) {
      const subCategoryField = formField?.formFieldGroup?.find((field: AnyType) => field.formFieldName === 'subcategory');
      this.setFormFieldValue(formGroup.stepperGroupName, formField.formFieldName, rowIndex, 'subcategory');
      const subCategoryList = getSubCategoryList(event.value.name.toLowerCase());

      const idx = subCategoryList.findIndex((options: AnyType) => options._id == MULTI_VALUE);
      if (idx == -1 && this.existingAssetListFiles.length > 1 && rowIndex === undefined) {
        subCategoryList.push({ _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE, products: [] });
      }
      subCategoryField.options = subCategoryList;
      if (subCategoryField.nestedOptions && rowIndex != undefined) subCategoryField.nestedOptions[rowIndex] = subCategoryList;
      if (rowIndex != undefined && subCategoryField) {
        if (event.value && subCategoryList.length) this.formFieldVisibility['subcategory'][rowIndex] = true;
        else this.formFieldVisibility['subcategory'][rowIndex] = false;
      }
    } else if (this.brandingKey === CustomClients.Arval) {
      this.assetCommonService.updateArvalFormData(formField.formFieldName, formGroup);
    }
  }

  onChannelSelected(event: AnyType, formField?: AnyType, formGroup?: AnyType) {
    if (this.brandingKey === CustomClients.Arval) {
      this.assetCommonService.updateArvalFormData(formField.formFieldName, formGroup);
    }
  }

  closeModal() {
    if (!this.stepperFormGroups.pristine) {
      this.dialog
        .open(ConfirmDialogComponent, {
          disableClose: true,
          data: {
            title: $localize`Confirm`,
            message: $localize`Are you sure you want to close? All unsaved data will be lost`,
            confirmCaption: PROMPTS.LABELS.YES,
            cancelCaption: PROMPTS.LABELS.NO,
          },
        })
        .afterClosed()
        .subscribe((data) => {
          if (data) this.dialogRef.close();
        });
    } else {
      this.dialogRef.close();
    }
  }

  getRequiredErrorMessage(formArrayControls: AbstractControl<AnyType, AnyType>, controlName: string) {
    return formArrayControls.get(controlName)?.hasError('required');
  }

  showSelectedStepper(stepDetails: StepperGroupModel, index: number) {
    if (index !== 0) {
      const groupName = this.initialStepper.stepper[index - 1].stepperGroupName;
      if (this.stepperFormGroups.controls[groupName].invalid) return;
    }
    this.stepperHeader = !stepDetails.hideHeader ? stepDetails.stepperName : '';
    if (stepDetails.overrideHeader) {
      this.stepperHeader = stepDetails.overrideHeader;
    }
    this.assetStepperDialogService.resetAllSteps(this.initialStepper, index);
    this.stepChange.emit(stepDetails);
  }

  /**
   * Move to next step
   * @param action
   * @param control
   * @returns
   */
  async nextGoToStep(action: ActionsModel, control?: AbstractControl<AnyType, AnyType>) {
    const firstRequiredControl = this.commonUtilityLib.getRequiredControls(control as UntypedFormGroup);
    if (firstRequiredControl) {
      const invalidFormField = this.el.nativeElement.querySelector('[id="stepper-dialog_' + firstRequiredControl + '"]');
      if (invalidFormField) invalidFormField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (this.stepperType === 'relations') {
      if (this.assetRelationsFormControl?.formArray.invalid) {
        this.invalidFormState.next(true);
        return;
      }
      if (this.assetRelationsFormControl?.formArray.length) {
        this.stepperFormGroups?.get('defineAssetRelation')?.get('assetRelation')?.patchValue({ assets: this.assetRelationsFormControl.formArray.value });
      }
    }

    if (control?.invalid) {
      control.markAllAsTouched();
      control.markAsDirty();
      this.fileUploadRequired = true;
      this.isMatTreeSelected = true;
      this.formControlValidState = true;
      this.invalidFormState.next(true); // an reactive way to trigger validation message in the child component (used for relation component)
      return;
    }
    this.formControlValidState = false;
    switch (action && action?.actionType) {
      case 'uploadAsset': {
        if (control) control.patchValue({ disabled: true });
        const formValueObject = this.assetStepperDialogService.getFormDirtyValuesEdits(this.stepperFormGroups, this.domainsMatchingFlag);
        this.formSubmit.emit(formValueObject);
        break;
      }
      default: {
        const stepDetails = this.initialStepper.stepper[(action?.goToStep || 0) - 1];
        this.showSelectedStepper(stepDetails, (action?.goToStep || 0) - 1);
        break;
      }
    }
  }

  /**
   * check for field validation
   * @param formField
   * @returns
   */
  getFieldValidation(formField: AbstractControl<AnyType, AnyType> | null): boolean {
    return formField?.errors?.['required'] && formField?.touched;
  }

  /**
   * Add tags
   * @param event
   * @param stepperGroupName
   * @param fieldName
   * @returns
   */
  async add(event: AnyType, stepperGroupName: string, fieldName: string) {
    if (event) {
      if (this.tags.includes(event)) return;
      if (this.env.feature?.tagGroups === true && event._id) {
        await this.coreAssetsTagsService.handleTagAddition(event, this.tags);
      } else {
        addTagToArray(event, this.tags);
      }
      this.updateTagsArray(this.tags, stepperGroupName, fieldName);
    } else {
      this.markFieldAsDirty(stepperGroupName, fieldName);
    }
  }

  /**
   * Add collection tags
   * @param event
   * @param stepperGroupName
   * @param fieldName
   * @returns
   */
  async addKeyword(event: AnyType, stepperGroupName: string, fieldName: string) {
    if (!event || this.collectionTags.includes(event)) return;

    if (this.env.feature?.tagGroups === true && event._id) {
      await this.coreAssetsTagsService.handleTagAddition(event, this.collectionTags);
    } else {
      addTagToArray(event, this.collectionTags);
    }

    this.updateTagsArray(this.collectionTags, stepperGroupName, fieldName, { refreshUI: false });
  }

  private updateTagsArray(tagsArray: string[], stepperGroupName: string, fieldName: string, options = { refreshUI: true }) {
    const formGroup = this.stepperFormGroups.controls[stepperGroupName];

    this.coreAssetsTagsService.updateTagsArray(tagsArray, formGroup, fieldName);

    if (options.refreshUI) this.refreshTagList();
  }

  private refreshTagList() {
    if (this.tagsChipComponent) this.tagsChipComponent.fetchTagsWithAssetCount();
    if (this.tagsChipsGridRow) this.tagsChipsGridRow.chipInput.nativeElement.value = '';
  }

  markFieldAsDirty(stepperGroupName: string, fieldName: string) {
    this.stepperFormGroups.markAsDirty();
    this.stepperFormGroups.markAllAsTouched();
    this.stepperFormGroups.controls[stepperGroupName].markAsDirty();
    this.stepperFormGroups.controls[stepperGroupName].markAllAsTouched();
    this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.markAsDirty();
  }

  tagOptionChanged(evt: AnyType, stepperGroupName: string, fieldName: string) {
    if (evt.fieldSelected) {
      this.add(evt.name, stepperGroupName, fieldName);
    } else {
      if (evt.createdAt) {
        this.removeTag(evt.name, stepperGroupName, fieldName);
      } else {
        this.addTags(evt.name, stepperGroupName, fieldName);
      }
    }
  }

  updateTagName(evt: AnyType, stepperGroupName: string, fieldName: string) {
    const tagIndex = this.tags.findIndex((tag: string) => tag === evt.oldTagName);
    if (tagIndex != -1) {
      this.tags[tagIndex] = evt.newTagName;
    }
    this.assetTagsService.updateTagsArray(this.tags);
    this.stepperFormGroups.controls[stepperGroupName].patchValue(this.tags);
    this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.markAsDirty();
  }

  /**
   * Pagination change event
   * @param event
   */
  onPaginateChange(event: PageEvent) {
    this.paginatorObj = event;
  }

  /**
   * Add tags tags
   * @param event
   * @param stepperGroupName
   * @param fieldName
   * @returns
   */
  addTags(event: string, stepperGroupName: string, fieldName: string): void {
    if (event) {
      if (this.tags.includes(event)) {
        return;
      }
      this.tags.push(event);
      this.tags.sort((a, b) => a.localeCompare(b));
      const controlValues = this.stepperFormGroups.controls[stepperGroupName].value;
      controlValues[fieldName] = this.tags;
      this.stepperFormGroups.controls[stepperGroupName].patchValue(controlValues);
      this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.markAsDirty();
      this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.markAsTouched();
    }
  }

  removeTagOnChange(evt: AnyType, stepperGroupName: string, fieldName: string) {
    this.removeTag(evt.list.name || evt.list, stepperGroupName, fieldName);
  }

  /**
   * Remove tags / keywords
   * @param key
   * @param stepperGroupName
   * @param fieldName
   */
  removeTag(key: string, stepperGroupName: string, fieldName: string): void {
    const tagsRef = stepperGroupName === 'createNewCollectionMetadata' ? this.collectionTags : this.tags;
    const index = tagsRef.indexOf(key);
    if (index >= 0) {
      // update tags list in TagsChipComponent on tag removal from ChipsGridRowComponent
      if (this.tagsChipComponent) this.tagsChipComponent.removeSelectedTags(key);
      tagsRef.splice(index, 1);
      tagsRef.sort((a, b) => a.localeCompare(b));
      this.stepperFormGroups.controls[stepperGroupName].patchValue({
        [fieldName]: tagsRef,
      });
      this.markFieldAsDirty(stepperGroupName, fieldName);
    }
  }

  /**
   * Remove chip from multi select
   * @param value
   */
  removeMultiSelectChip(value: AnyType, stepperGroupName: string, fieldName: string) {
    const chip = this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.value;
    const modifiedChip = this.commonUtilityLib.removeObjectFromArray(chip, value.list.id || value.list._id || value.list);
    this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.setValue(modifiedChip);
    this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.markAsDirty();
    this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.markAsTouched();
  }

  onDateSelection(event: MatDatepickerInputEvent<AnyType, AnyType>, formFieldName: string, formGroupName: string) {
    const formGroupField = this.stepperFormGroups.controls[formGroupName] as UntypedFormGroup;
    if (formFieldName === 'publishStartDate') {
      const publishStartDateField = formGroupField?.controls['publishStartDate'];
      const publishEndDateField = formGroupField?.controls['publishEndDate'];
      if (publishStartDateField && publishEndDateField) {
        if (publishEndDateField.value && publishEndDateField.value < event.value) {
          publishStartDateField.setValue('');
          console.log('The start date can not be set after the end date.');
        }
      }
      if (publishStartDateField.value) {
        publishStartDateField.setValidators(minDateValidator(publishStartDateField));
        publishStartDateField.updateValueAndValidity();
      }
    } else if (formFieldName === 'publishEndDate') {
      const publishStartDateField = formGroupField?.controls['publishStartDate'];
      const publishEndDateField = formGroupField?.controls['publishEndDate'];
      if (publishStartDateField && publishEndDateField) {
        if (publishStartDateField.value && publishStartDateField.value > event.value) {
          publishEndDateField.setValue('');
          console.log('The end date can not be set before the start date.');
        }
      }
      if (publishEndDateField.value) {
        publishEndDateField.setValidators(minDateValidator(publishEndDateField));
        publishEndDateField.updateValueAndValidity();
      }
    } else if (formFieldName === 'expiryDate') {
      const noExpiryField = formGroupField?.controls['noExpiry'];
      if (event.value) {
        noExpiryField?.patchValue(false);
      }
    }
  }

  /**
   * mat-checkbox capture change event
   * @param event
   * @param stepperGroup
   * @param formFieldName
   */

  checkboxChanged(event: AnyType, formFieldName: string, formGroupName: string) {
    const formGroupField = this.stepperFormGroups.controls[formGroupName] as UntypedFormGroup;
    formGroupField.patchValue({ [formFieldName]: event.checked });
    const plannedDeletionField = formGroupField?.controls['plannedDeletion'];
    if (plannedDeletionField) {
      if (event.checked) {
        plannedDeletionField.setValue('');
        plannedDeletionField.enable();
        plannedDeletionField.setValidators(Validators.required);
      } else {
        plannedDeletionField.setValue('');
        plannedDeletionField.disable();
        plannedDeletionField.clearValidators();
      }
      plannedDeletionField.updateValueAndValidity();
    } else {
      const expiryDateField = formGroupField?.controls['expiryDate'];
      if (expiryDateField && event.checked) {
        expiryDateField?.clearValidators();
        expiryDateField.reset();
        expiryDateField.markAsDirty();
      } else {
        expiryDateField.setValidators(Validators.required);
        expiryDateField.markAsDirty();
      }
      expiryDateField.updateValueAndValidity();
    }
  }

  /**
   * If mat tree status changes (checked or unchecked)
   * @param event
   * @param stepperGroupName
   * @param fieldName
   */
  changeStatus(event: boolean, stepperGroupName: string, fieldName: string) {
    this.disableButtons = !event;
    if (!event) {
      this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.setErrors({ required: true });
    } else {
      this.stepperFormGroups.controls[stepperGroupName].get(fieldName)?.setErrors(null);
    }
  }

  /**
   * MaT tree returns checked nodes
   * @param event
   */
  selectedFormatList(event: AspectRationFormatTree[]) {
    this.matTreeRawData = event;
    if (this.matTreeRawData.length) this.isMatTreeSelected = false;
  }

  /**
   * Set values in listUsers from sp-user-email-input-autocomplete component
   * @param user
   */
  userSelected(user: string[]) {
    this.listUsers = user;
  }

  /**
   * store description text from sp-editor change
   * @param descriptionText
   */
  editorDescriptionText(descriptionText: string) {
    this.descriptionText.emit(descriptionText);
  }

  /**
   * Assign paginator to MatTableDataSource paginator
   */
  paginatorAssignment(event: { selectedTagsList: TagsWithAssetCount[] }) {
    setTimeout(() => {
      this.dataSource = new MatTableDataSource<TagsWithAssetCount>(event.selectedTagsList);
      this.dataSource.paginator = this.paginator;
      this.tagsObs = this.dataSource.connect();
    });
  }

  searchedTags(event: { searchTagsList: TagsWithAssetCount[]; selectedTagName?: string }) {
    this.searchTagsList = event.searchTagsList;
  }

  restrictInput(event: KeyboardEvent): void {
    const allowedPattern = /^[0-9-]$/; // Allow only numbers and hyphen
    if (!allowedPattern.test(event.key)) {
      event.preventDefault();
    }
  }

  inputBlurOut() {
    this.tagsChipComponent.fetchTagsWithAssetCount();
  }

  openTaggingGuidelines() {
    const pdfUrl = 'assets/tagging_guidelines/Verschlagwortungskonzept.pdf';
    window.open(pdfUrl, '_blank');
  }

  openNamingGuidelines() {
    const pdfUrl = 'assets/naming_guidelines/Benamungskonzept.pdf';
    window.open(pdfUrl, '_blank');
  }

  trimFormControlValue(controlName: string, groupName: string) {
    const control = this.stepperFormGroups.controls[groupName].get(controlName);
    if (control && typeof control.value === 'string') {
      control.setValue(control.value.trim());
    }
  }
}
